/**
 * /me/leaves/new — Employee Request Leave Wizard (M3X.LEAVES PR4 — F.3).
 *
 * Multi-section ESS wizard per pack §1.14:
 *   1. Leave Type — picker from active leave_types
 *   2. Dates — from/to + computed days
 *   3. Supporting Documents — drag-drop placeholder
 *   4. Reason — free text + Solvait AI ✦ assist
 *   5. Review & Submit — full summary
 * Form-completion progress + save-draft (localStorage).
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui/PageHeader";
import { api } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

type Section = "type" | "dates" | "docs" | "reason" | "review";

const SECTIONS: { id: Section; labelKey: string; defaultLabel: string }[] =
  [
    { id: "type", labelKey: "wiz_leave.type", defaultLabel: "Leave Type" },
    { id: "dates", labelKey: "wiz_leave.dates", defaultLabel: "Dates" },
    { id: "docs", labelKey: "wiz_leave.docs", defaultLabel: "Documents" },
    { id: "reason", labelKey: "wiz_leave.reason", defaultLabel: "Reason" },
    { id: "review", labelKey: "wiz_leave.review", defaultLabel: "Review & Submit" },
  ];

interface Draft {
  leave_type_id: string;
  start_date: string;
  end_date: string;
  reason: string;
}

const EMPTY: Draft = {
  leave_type_id: "",
  start_date: "",
  end_date: "",
  reason: "",
};

const LS_KEY = "m3x_leaves_pr4_draft";

function loadDraft(): Draft {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...EMPTY, ...(JSON.parse(raw) as Partial<Draft>) };
  } catch {
    /* swallow */
  }
  return EMPTY;
}

function saveDraft(d: Draft): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(d));
  } catch {
    /* swallow */
  }
}

function daysBetween(start: string, end: string): number {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  return Math.max(0, Math.round((e.getTime() - s.getTime()) / 86400000) + 1);
}

export function RequestLeaveWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const employeeId = search.get("employee_id") ?? "";
  const legalEntityId = useLegalEntityId() ?? "";

  const [section, setSection] = useState<Section>("type");
  const [draft, setDraft] = useState<Draft>(() => loadDraft());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const typesQuery = useQuery({
    queryKey: ["leave-types-for-wizard", legalEntityId],
    queryFn: () => api.listLeaveTypes(legalEntityId),
    enabled: Boolean(legalEntityId),
  });

  const updateDraft = (patch: Partial<Draft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    saveDraft(next);
  };

  const computedDays = daysBetween(draft.start_date, draft.end_date);

  const progressPct = useMemo(() => {
    let done = 0;
    if (draft.leave_type_id) done += 25;
    if (draft.start_date) done += 25;
    if (draft.end_date) done += 25;
    if (draft.reason.trim().length > 0) done += 25;
    return done;
  }, [draft]);

  const canSubmit =
    draft.leave_type_id !== "" &&
    draft.start_date !== "" &&
    draft.end_date !== "" &&
    computedDays > 0;

  const handleSubmit = async () => {
    if (!canSubmit || !employeeId || !legalEntityId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.createLeaveRequest({
        employee_id: employeeId,
        legal_entity_id: legalEntityId,
        leave_type_id: draft.leave_type_id,
        start_date: draft.start_date,
        end_date: draft.end_date,
        reason: draft.reason || null,
      });
      try {
        localStorage.removeItem(LS_KEY);
      } catch {
        /* swallow */
      }
      navigate(
        `/me/leaves?employee_id=${encodeURIComponent(employeeId)}`,
      );
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!employeeId || !legalEntityId) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        {t(
          "wiz_leave.missing_params",
          "Sign in to start a leave request. HR can start one for any employee from the Employees page.",
        )}
      </div>
    );
  }

  const selectedType = (typesQuery.data ?? []).find(
    (lt) => lt.id === draft.leave_type_id,
  );

  return (
    <div className="space-y-6 max-w-3xl mx-auto px-3 sm:px-0">
      <PageHeader
        title={t("wiz_leave.title", "Request a Leave")}
        subtitle={t(
          "wiz_leave.subtitle",
          "Tashghil checks eligibility live; Mutaba'aa enforces statutory floors.",
        )}
      />

      <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-medium text-text-tertiary uppercase">
            {t("wiz_leave.progress", "Form completion")}
          </div>
          <div className="text-xs font-mono">{progressPct}%</div>
        </div>
        <div className="h-2 bg-white border border-slate-300 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-blue"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <nav className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${
              section === s.id
                ? "border-brand-blue text-brand-deep font-semibold"
                : "border-transparent text-text-secondary"
            }`}
          >
            {t(s.labelKey, s.defaultLabel)}
          </button>
        ))}
      </nav>

      <div className="rounded-lg border border-slate-200 bg-surface-default p-4 sm:p-6">
        {section === "type" ? (
          typesQuery.isLoading ? (
            <Loading />
          ) : typesQuery.error ? (
            <ErrorPanel
              message={t(
                "wiz_leave.load_types_error",
                "Couldn't load leave types.",
              )}
            />
          ) : (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold">
                {t("wiz_leave.type_title", "Which leave?")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(typesQuery.data ?? []).map((lt) => (
                  <label
                    key={lt.id}
                    className={`block rounded-md border p-3 cursor-pointer ${
                      draft.leave_type_id === lt.id
                        ? "border-brand-blue ring-2 ring-brand-blue/30 bg-brand-blue/5"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="leave_type"
                      checked={draft.leave_type_id === lt.id}
                      onChange={() => updateDraft({ leave_type_id: lt.id })}
                      className="sr-only"
                    />
                    <div className="font-medium">{lt.name_en}</div>
                    <div className="text-xs text-text-tertiary">
                      {lt.name_ar} · {lt.code}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )
        ) : null}

        {section === "dates" ? (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold">
              {t("wiz_leave.dates_title", "When?")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-text-tertiary mb-1">
                  {t("wiz_leave.start_date", "Start date")}
                </label>
                <input
                  type="date"
                  value={draft.start_date}
                  onChange={(e) =>
                    updateDraft({ start_date: e.target.value })
                  }
                  className="w-full rounded-md border border-slate-300 px-2 py-1.5"
                />
              </div>
              <div>
                <label className="block text-xs text-text-tertiary mb-1">
                  {t("wiz_leave.end_date", "End date")}
                </label>
                <input
                  type="date"
                  value={draft.end_date}
                  onChange={(e) =>
                    updateDraft({ end_date: e.target.value })
                  }
                  className="w-full rounded-md border border-slate-300 px-2 py-1.5"
                />
              </div>
            </div>
            <div className="text-sm text-text-secondary">
              {t("wiz_leave.computed_days", "Days requested:")}{" "}
              <span className="font-mono font-semibold">{computedDays}</span>
            </div>
          </div>
        ) : null}

        {section === "docs" ? (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold">
              {t("wiz_leave.docs_title", "Supporting documents")}
            </h2>
            <div className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-text-tertiary">
              {t(
                "wiz_leave.docs_dropzone",
                "Drop a PDF / DOC / JPG (max 10 MB) — coming soon",
              )}
            </div>
          </div>
        ) : null}

        {section === "reason" ? (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              {t("wiz_leave.reason_title", "Reason")}
              <button
                onClick={() =>
                  updateDraft({
                    reason: t(
                      "wiz_leave.ai_assist_stub",
                      "Family medical emergency requiring my immediate presence.",
                    ),
                  })
                }
                className="text-xs px-2 py-1 rounded-md bg-ai-bg text-ai-deep border border-ai-accent/30 hover:border-ai-accent"
              >
                ✦ {t("wiz_leave.ai_assist", "Solvait AI assist")}
              </button>
            </h2>
            <textarea
              value={draft.reason}
              onChange={(e) => updateDraft({ reason: e.target.value })}
              rows={4}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder={t(
                "wiz_leave.reason_placeholder",
                "Describe the reason for this leave (optional for some types).",
              )}
            />
          </div>
        ) : null}

        {section === "review" ? (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold">
              {t("wiz_leave.review_title", "Review & submit")}
            </h2>
            <dl className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-y-2">
              <dt className="text-text-tertiary">
                {t("wiz_leave.type", "Type")}:
              </dt>
              <dd>{selectedType?.name_en ?? "—"}</dd>
              <dt className="text-text-tertiary">
                {t("wiz_leave.dates", "Dates")}:
              </dt>
              <dd>{draft.start_date} → {draft.end_date}</dd>
              <dt className="text-text-tertiary">
                {t("wiz_leave.days", "Days")}:
              </dt>
              <dd className="font-mono">{computedDays}</dd>
              <dt className="text-text-tertiary">
                {t("wiz_leave.reason", "Reason")}:
              </dt>
              <dd className="whitespace-pre-wrap">{draft.reason || "—"}</dd>
            </dl>
            {submitError ? (
              <ErrorPanel
                message={
                  t("wiz_leave.submit_error", "Submission failed:") +
                  " " +
                  submitError
                }
              />
            ) : null}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="w-full rounded-md bg-brand-blue text-white py-2 font-medium hover:bg-brand-deep disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting
                ? t("wiz_leave.submitting", "Submitting...")
                : t("wiz_leave.submit", "Submit leave request")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
